import { useAccountScope } from '../context/AccountScope';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import socket from '../services/socket';
import './NotificationBell.css';

const tokenHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function icon(type) {
  if (String(type).startsWith('CENTINELA_')) return '🛡️';
  if (type === 'VEHICULO_ENCENDIDO') return '🚗';
  if (type === 'VEHICULO_APAGADO') return '📍';
  return '🔔';
}

function localDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('es-CO', {
    timeZone: 'America/Bogota', day: 'numeric', month: 'short',
    hour: 'numeric', minute: '2-digit', hour12: true
  }).format(date);
}

export default function NotificationBell({ mobile = false, onOpen }) {
  const { fetch, account: scopeAccount } = useAccountScope();
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const requestRef = useRef(0);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 72, right: 12 });
  const [items, setItems] = useState([]);
  const [watermark, setWatermark] = useState(0);
  const [markingAll, setMarkingAll] = useState(false);
  const readingRef = useRef(new Set());
  const unread = items.filter(item => !item.readAt).length;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    const request = ++requestRef.current;
    setLoading(true);
    try {
      const response = await fetch('/api/notifications', {
        headers: tokenHeader()
      });
      if (!response.ok) throw new Error('No se pudieron cargar las notificaciones.');
      const data = await response.json();
      if (request !== requestRef.current) return;
      setItems(Array.isArray(data.items) ? data.items.slice(0, 10) : []);
      setWatermark(Number(data.watermark) || 0);
      setError('');
    } catch (cause) {
      if (cause?.name === 'AbortError') return;
      if (request === requestRef.current) setError(cause.message);
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const refresh = () => load();
    const onFocus = () => load();
    socket.on('vehicle-alert', refresh);
    socket.on('centinela-alert', refresh);
    window.addEventListener('focus', onFocus);
    const timer = setInterval(refresh, 60000);
    return () => {
      requestRef.current += 1;
      socket.off('vehicle-alert', refresh);
      socket.off('centinela-alert', refresh);
      window.removeEventListener('focus', onFocus);
      clearInterval(timer);
    };
  }, [load]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (rect) setPosition({ top: rect.bottom + 8,
        right: Math.max(10, window.innerWidth - rect.right) });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const outside = event => {
      if (!buttonRef.current?.contains(event.target) &&
          !panelRef.current?.contains(event.target)) setOpen(false);
    };
    const escape = event => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  async function select(item) {
    setSelectedId(value => value === item.id ? null : item.id);
    if (item.readAt || readingRef.current.has(item.id) || markingAll) return;
    readingRef.current.add(item.id);
    try {
      const response = await fetch(`/api/notifications/${item.id}/read`, {
        method: 'PATCH', headers: tokenHeader()
      });
      if (!response.ok) throw new Error('No se pudo marcar como leída.');
      setItems(previous => previous.map(entry => entry.id === item.id
        ? { ...entry, readAt: new Date().toISOString() } : entry));

    } catch (cause) {
      if (cause?.name === 'AbortError') return; setError(cause.message);
    } finally { readingRef.current.delete(item.id); }
  }

  async function markAllRead() {
    if (markingAll || loading || !unread) return;
    setMarkingAll(true); setError('');
    // Cancel older loads so that their unread states cannot overwrite this action.
    ++requestRef.current;
    try {
      const response = await fetch('/api/notifications/read-all', {
        method: 'PATCH', headers: { ...tokenHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ throughId: watermark })
      });
      if (!response.ok) throw new Error('No se pudieron marcar como leídas.');
      const time = new Date().toISOString();
      setItems(previous => previous.map(item => Number(item.id) <= watermark
        ? { ...item, readAt: item.readAt || time } : item));
      await load();
    } catch (cause) {
      if (cause?.name !== 'AbortError') setError(cause.message);
    } finally { setMarkingAll(false); }
  }

  return <>
    <button ref={buttonRef} type="button" aria-label="Notificaciones"
      aria-expanded={open} title="Notificaciones"
      className={`maccez-notification-bell ${mobile ? 'mobile' : ''}`}
      onClick={() => {
        setOpen(value => !value);
        if (!open) { onOpen?.(); load(); }
      }}>
      <svg width="23" height="23" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4"
          stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {unread > 0 && <span className="maccez-notification-count">{unread > 99 ? '99+' : unread}</span>}
    </button>
    {open && createPortal(
      <div ref={panelRef} className="maccez-notification-panel" role="dialog"
        aria-label="Notificaciones" style={{ top: position.top, right: position.right,
          maxHeight: `min(60dvh, ${Math.max(150, window.innerHeight - position.top - 106)}px)` }}>
        <div className="maccez-notification-head">
          <div><strong>Notificaciones</strong><small>Actividad reciente · {scopeAccount.name}</small></div>
          <button type="button" onClick={() => setOpen(false)} aria-label="Cerrar notificaciones">×</button>
        </div>
        <div className="maccez-notification-actions">
          <span>Últimas 10</span>
          <button type="button" disabled={markingAll || loading || !unread}
            onClick={markAllRead}>{markingAll ? 'Actualizando…' : 'Marcar todas como leídas'}</button>
        </div>
        <div className="maccez-notification-list">
          {!loading && !items.length && !error && <p className="maccez-notification-empty">Aún no hay notificaciones.</p>}
          {items.map(item => <div className={`maccez-notification-item ${item.readAt ? '' : 'fresh'}`}
            key={item.id}>
            <button type="button" className="maccez-notification-row" onClick={() => select(item)}
              aria-expanded={selectedId === item.id}>
              <span className="maccez-notification-icon" aria-hidden="true">{icon(item.type)}</span>
              <span className="maccez-notification-copy">
                <span className="maccez-notification-title">{item.vehicleName} <span>· {item.title}</span></span>
                <span className="maccez-notification-summary">{item.summary}</span>
                <time>{localDate(item.occurredAt)}</time>
              </span>
              {!item.readAt && <span className="maccez-notification-dot" aria-label="Nueva" />}
            </button>
            {selectedId === item.id && <div className="maccez-notification-detail">{item.message}</div>}
          </div>)}
          {error && <p className="maccez-notification-error">{error}</p>}
          {loading && <p className="maccez-notification-empty">Cargando…</p>}
        </div>
      </div>, document.body)}
  </>;
}
